import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { useForm, Controller } from 'react-hook-form';

import { Button, Grid, Input, Margin, Modal, ModalHeader, ModalBody, ModalFooter } from '@gbm/queen-ui-guidelines';
import DropdownController from 'components/DropdownController';

const applicationTypeOptions = ['web', 'mobile', 'api', 'desktop', 'service', 'worker', 'batch', 'iot', 'cli'].map(
  option => ({
    value: option,
    label: option,
  })
);

export function ApplicationModal({ open, onClose, onSubmit, title, loading, error, appToEdit }) {
  const isEditMode = Boolean(appToEdit);
  const { handleSubmit, register, errors, formState, reset, control } = useForm({ mode: 'onChange' });
  const { isValid, isDirty } = formState;

  useEffect(() => {
    const selectedType = applicationTypeOptions.find(option => option.value === appToEdit?.type) || '';

    const fillForm = async () => {
      await reset({
        clientId: appToEdit?.clientId || appToEdit?.client_id || '',
        app: appToEdit?.app || appToEdit?.name || appToEdit?.application || '',
        displayName: appToEdit?.displayName || appToEdit?.display_name || appToEdit?.application || '',
        type: selectedType,
      });
    };

    fillForm();
  }, [appToEdit, reset]);

  return (
    <Modal id="modalHandleApp" isOpen={open} size="md" centered>
      <ModalHeader onClose={onClose}>{title}</ModalHeader>
      <ModalBody>
        <Grid container>
          <Grid row>
            <Grid sm={12}>
              <Margin side="bottom" xs={24}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {!isEditMode && (
                    <>
                      <Input
                        id="clientId"
                        name="clientId"
                        label="GBM Pod"
                        required={!isEditMode}
                        placeholder="Enter GBM Pod"
                        disabled={isEditMode}
                        error={Boolean(errors.clientId)}
                        errorMsg={errors.clientId?.message}
                        ref={register({
                          required: !isEditMode && 'GBM Pod is required',
                          minLength: {
                            value: 1,
                            message: 'GBM Pod must have at least 1 character',
                          },
                        })}
                      />

                      <Margin side="top" xs={24}>
                        <Input
                          id="app"
                          name="app"
                          label="Nombre de la aplicación"
                          required={!isEditMode}
                          placeholder="Enter application name"
                          disabled={isEditMode}
                          error={Boolean(errors.app)}
                          errorMsg={errors.app?.message}
                          ref={register({
                            required: !isEditMode && 'Application name is required',
                            minLength: {
                              value: 2,
                              message: 'Application name must have at least 2 characters',
                            },
                          })}
                        />
                      </Margin>
                    </>
                  )}

                  <Margin side="top" xs={24}>
                    <Input
                      id="displayName"
                      name="displayName"
                      label="Descripción"
                      required
                      placeholder="Enter description"
                      error={Boolean(errors.displayName)}
                      errorMsg={errors.displayName?.message}
                      ref={register({
                        required: 'Description is required',
                        minLength: {
                          value: 3,
                          message: 'Description must have at least 3 characters',
                        },
                      })}
                    />
                  </Margin>

                  {!isEditMode && (
                    <Margin side="top" xs={24}>
                      <Controller
                        control={control}
                        name="type"
                        rules={{
                          required: !isEditMode && 'Type is required',
                          validate: value => (value && value.value ? true : 'Type is required'),
                        }}
                        defaultValue=""
                        render={({ onChange, value, name }) => (
                          <DropdownController
                            id="applicationType"
                            name={name}
                            label="Type"
                            placeholder={value?.label || 'Select a type'}
                            options={applicationTypeOptions}
                            required={!isEditMode}
                            disabled={loading}
                            onChange={onChange}
                          />
                        )}
                      />
                    </Margin>
                  )}
                </form>
              </Margin>
            </Grid>
          </Grid>
        </Grid>
      </ModalBody>

      <ModalFooter>
        <Button id="btnCancelApp" type="button" onClick={onClose} disabled={loading} color="light">
          Cancel
        </Button>

        <Button
          id="btnSubmitApp"
          type="button"
          disabled={!isValid || !isDirty || loading}
          loading={loading}
          onClick={handleSubmit(onSubmit)}>
          {error ? 'Retry' : 'Accept'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

ApplicationModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  title: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  appToEdit: PropTypes.object,
};
